import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import useDashboardData from "../hooks/useDashboardData";
import EnhancedChart from "./EnhancedChart";
import "./Dashboard.css";

function ProductDashboard() {
  const {
    data: chartsData,
    loading,
    error,
    refetch,
  } = useDashboardData("/visualize/products", { method: "POST" });
  const [stdSummary, setStdSummary] = useState(null);

  useEffect(() => {
    fetchStandardSummary();
  }, []);

  const fetchProductData = refetch;

  const fetchStandardSummary = async () => {
    try {
      const res = await fetch("https://restaurant-analytics-backend.onrender.com/report/summary", {
        method: "GET",
      });
      if (res.status === 404) return; // No summary yet
      const data = await res.json();
      if (res.ok) {
        setStdSummary(data);
      } else {
        throw new Error(data.detail || "Failed to fetch standardized summary");
      }
    } catch (err) {
      console.error("Error fetching summary:", err.message || String(err));
    }
  };



  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <h2>Loading Product Analytics...</h2>
        <p>Analyzing product performance and generating insights</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <h2>❌ Error Loading Product Data</h2>
        <p>{error}</p>
        <p>Make sure you have uploaded both sales data and product costs.</p>
        <button onClick={fetchProductData} className="retry-btn">
          🔄 Retry
        </button>
      </div>
    );
  }

  if (!chartsData || !chartsData.charts) {
    return (
      <div className="dashboard-error">
        <h2>🛍️ No Product Data Available</h2>
        <p>
          Please upload your sales data and product costs first to view product
          analytics
        </p>
      </div>
    );
  }

  const { charts, data_summary } = chartsData;

  // Fallbacks when summary not present
  const fallbackProductStats = (() => {
    let records = data_summary?.total_records ?? null;
    let uniqueProducts = data_summary?.unique_products ?? null;

    // Infer from one of the charts (e.g., product_revenue_ranking)
    const pr = charts?.product_revenue_ranking;
    const labels = pr?.data?.labels;
    const ds = pr?.data?.datasets?.[0]?.data;
    if (records == null) {
      if (Array.isArray(ds) && ds.length > 0) records = ds.length;
      else if (Array.isArray(labels) && labels.length > 0)
        records = labels.length;
    }
    if (uniqueProducts == null) {
      if (Array.isArray(labels)) uniqueProducts = new Set(labels).size;
      else uniqueProducts = records ?? 0;
    }
    return { records, uniqueProducts };
  })();

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>🛍️ Product Performance Dashboard</h1>
        <p>
          Comprehensive analysis of your product portfolio and performance
          metrics
        </p>

        {/* Standardized product summary */}
        {stdSummary &&
          (() => {
            const summaries = stdSummary.summaries || {};
            const products = summaries.product_performance || {};
            const totalRevenue = products.total_revenue ?? null;
            const totalProfit = products.total_profit ?? null;
            const avgMargin = products.avg_margin ?? null;
            return (
              <div className="summary-cards" style={{ marginTop: "0.5rem" }}>
                {totalRevenue != null && (
                  <div className="summary-card">
                    <span className="summary-icon">💰</span>
                    <div className="summary-content">
                      <h3>₹{Number(totalRevenue).toLocaleString()}</h3>
                      <p>Total Revenue</p>
                    </div>
                  </div>
                )}
                {totalProfit != null && (
                  <div className="summary-card">
                    <span className="summary-icon">📈</span>
                    <div className="summary-content">
                      <h3>₹{Number(totalProfit).toLocaleString()}</h3>
                      <p>Total Profit</p>
                    </div>
                  </div>
                )}
                {avgMargin != null && (
                  <div className="summary-card">
                    <span className="summary-icon">📊</span>
                    <div className="summary-content">
                      <h3>{Number(avgMargin).toFixed(1)}%</h3>
                      <p>Average Margin</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

        <div className="summary-cards">
          <div className="summary-card">
            <span className="summary-icon">📦</span>
            <div className="summary-content">
              <h3>
                {Number(fallbackProductStats.records ?? 0).toLocaleString()}
              </h3>
              <p>Total Records</p>
            </div>
          </div>

          <div className="summary-card">
            <span className="summary-icon">🏷️</span>
            <div className="summary-content">
              <h3>
                {Number(
                  fallbackProductStats.uniqueProducts ?? 0
                ).toLocaleString()}
              </h3>
              <p>Unique Products</p>
            </div>
          </div>
        </div>
      </div>

      <motion.div 
        className="charts-grid"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      >
        {charts.product_revenue_ranking && (
          <EnhancedChart
            chartData={charts.product_revenue_ranking}
            title="Top Products by Revenue"
            preferredType="auto"
            showMetrics={true}
          />
        )}
        {charts.product_margin_analysis && (
          <EnhancedChart
            chartData={charts.product_margin_analysis}
            title="Product Margin Analysis"
            preferredType="auto"
            showMetrics={true}
          />
        )}
        {charts.product_performance_matrix && (
          <EnhancedChart
            chartData={charts.product_performance_matrix}
            title="Product Performance Matrix"
            preferredType="plotly"
            showMetrics={false}
          />
        )}
        {charts.category_performance && (
          <EnhancedChart
            chartData={charts.category_performance}
            title="Category Performance"
            preferredType="auto"
            showMetrics={true}
          />
        )}
        {charts.product_sales_velocity && (
          <EnhancedChart
            chartData={charts.product_sales_velocity}
            title="Product Sales Velocity"
            preferredType="auto"
            showMetrics={true}
          />
        )}
      </motion.div>

      <div className="dashboard-actions">
        <button onClick={fetchProductData} className="refresh-btn">
          🔄 Refresh Data
        </button>
      </div>
    </div>
  );
}

export default ProductDashboard;
